import {
  clearCart,
  closeCart,
  schema,
  selectCartItems,
  selectCartStatus,
  selectCity,
  selectOrderId,
  selectPoint,
} from '@entities/cart';
import { useModules } from '@entities/common';
import { orderApi } from '@entities/orders';
import { productApi } from '@entities/products';
import { yupResolver } from '@hookform/resolvers/yup';
import type { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import xhr from '@shared/config/api';
import { useAppDispatch, useAppSelector, useDialog } from '@shared/hooks';
import { useModuleByCodeParam } from '@shared/hooks/use-module-by-code-param.ts';
import type { CartValues } from '@shared/types/cart';
import type { CDEKCity, CDEKPickupPoint } from '@shared/types/delivery';
import { useCertificateMessage } from '@src/library/hooks/common';
import { clearShop } from '@src/library/redux/shop';

export const useCart = () => {
  const navigate = useNavigate();

  const status = useAppSelector(selectCartStatus);
  const items = useAppSelector(selectCartItems);
  const city = useAppSelector(selectCity);
  const point = useAppSelector(selectPoint);

  const { handleSubmit, control, setValue, ...methods } = useForm<CartValues>({
    defaultValues: {
      city: {} as CDEKCity,
      pickup_point: {} as CDEKPickupPoint,
    },
    resolver: yupResolver<any>(schema),
  });

  const close = () => {
    navigate(-1);
  };

  useEffect(() => {
    setValue('city', city ? city : ({} as CDEKCity));
    setValue('pickup_point', point ? point : ({} as CDEKPickupPoint));
  }, [setValue, city, point]);

  return {
    close,
    control,
    handleSubmit,
    status,
    items,
    methods: { handleSubmit, control, setValue, ...methods },
  };
};

export const useCheckout = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { dialog, notification } = useDialog();

  const items = useAppSelector(selectCartItems);
  const id = useAppSelector(selectOrderId);

  const { modules } = useModules();
  const storeModule = modules.find((module) => module.module_type === 'shop');
  const { permissions } = useModuleByCodeParam();

  const [isPaymentOpen, setIsPaymentOpen] = useState<boolean>(false);

  const { onRedirect } = useCertificateMessage();

  const isPartner = permissions.can_sell;

  const [loading, setLoading] = useState<boolean>(false);

  // const applyCode = () => dispatch(applyPromo(code));

  const ordersUrl = `/shop/${storeModule?.code}/orders`;

  const partnerPay = async () => {
    setLoading(true);
    try {
      const response = await xhr.get(`${productApi}/${id}/special_buy/`);

      if (!response.data) {
        throw response;
      }

      dispatch(closeCart());
      dispatch(clearCart());
      navigate(ordersUrl);
      dispatch(clearShop());

      notification({
        title: 'Заказ успешно создан',
        text: 'Менеджер магазина свяжется с Вами в самое ближайшее время для уточнения деталей заказа',
        confirmText: 'Ок',
      });
    } catch (error) {
      const err = error as AxiosError<any>;
      if (err.response?.status === 400) {
        if (err.response.data[0].message === 'Product position absent in storage') {
          notification({
            title: '',
            text: 'Недостаточно товара на складе',
            confirmText: 'Ок',
          });

          // for (const message of err.response.data) {
          // 	// dispatch(setError(message));
          // }
        }
      }
    }
    setLoading(false);
  };

  const buyerPay = async () => {
    // setIsPaymentOpen(true);
    try {
      const response = await xhr.get(`${productApi}/${id}/buy/`);
      if (!response.data) {
        throw response;
      }

      // setUrl(response.data.redirect_url);
      if (response.data.redirect_url) {
        return response.data.redirect_url;
      }
    } catch (error) {
      const err = error as AxiosError<any>;
      if (err.response?.status === 400) {
        let message = '';
        if (err.response.data[0].message === 'Product position absent in storage') {
          message = 'Недостаточно товара на складе';
          // for (const message of err.response.data) {
          // 	// dispatch(setError(message));
          // }
        } else {
          message = 'Ошибка подключения к системе оплаты. Попробуйте позже';
        }

        notification({
          title: '',
          text: message,
          confirmText: 'Ок',
        });
      }
      setIsPaymentOpen(false);
    }
  };

  const payOrder = async () => {
    if (isPartner) {
      partnerPay();
    } else {
      onRedirect(() => buyerPay());
    }
  };

  const onPaySuccess = async () => {
    try {
      await xhr.patch(`${orderApi}/${id}/transaction_status/`);
    } catch (error) {}

    setIsPaymentOpen(false);
    dispatch(closeCart());
    dispatch(clearCart());
    dispatch(clearShop());
    navigate(ordersUrl);

    notification({
      title: '',
      text: 'Ваш заказ успешно оплачен',
      confirmText: 'Ок',
    });
  };

  const confirmAquiringClose = () => {
    setIsPaymentOpen(false);
    dispatch(closeCart());
    dispatch(clearCart());
    dispatch(clearShop());
  };

  const onClose = () => {
    dialog({
      title: 'Закрыть корзину',
      text: 'Вы действительно хотите закрыть корзину? Ваш заказ сохранится на вкладке "Мои заказы", где его можно будет оплатить позднее',
      confirmText: 'Закрыть',
      confirm: () => confirmAquiringClose(),
      declineText: 'Отмена',
    });
  };

  return {
    items,
    isPartner,
    isPaymentOpen,
    setIsPaymentOpen,
    payOrder,
    onPaySuccess,
    onClose,
    loading,
  };
};
